import React from "react";
import {
  Banner,
  Badge,
  BlockStack,
  Box,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Grid,
  InlineStack,
  List,
  Modal,
  Text,
  TextField,
} from "@shopify/polaris";
import { DeleteIcon, EditIcon } from "@shopify/polaris-icons";
import type { HolidayEntry, TierKey } from "@reach/core";
import { PLAN_DETAILS } from "../planData";

type Provider = "stripe" | "lemonsqueezy" | null;

type PlanConfirmModalProps = {
  currentTier: TierKey;
  loading: boolean;
  open: boolean;
  pendingPlan: TierKey | null;
  tierKeys: readonly TierKey[];
  onClose: () => void;
  onConfirm: (planId: TierKey) => void | Promise<void>;
};

export function PlanConfirmModal({
  currentTier,
  loading,
  open,
  pendingPlan,
  tierKeys,
  onClose,
  onConfirm,
}: PlanConfirmModalProps) {
  const isDowngrade = pendingPlan ? tierKeys.indexOf(pendingPlan) < tierKeys.indexOf(currentTier) : false;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={pendingPlan === "free" ? "Cancel Subscription?" : `Switch to ${pendingPlan ? PLAN_DETAILS[pendingPlan].name : "this plan"}?`}
      primaryAction={{
        content: pendingPlan === "free" ? "Confirm Cancellation" : "Confirm Change",
        onAction: () => pendingPlan && onConfirm(pendingPlan),
        loading,
        destructive: isDowngrade,
      }}
      secondaryActions={[{ content: "Keep Current Plan", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {isDowngrade && pendingPlan ? (
            <>
              <Text as="p">
                You are downgrading from <strong>{PLAN_DETAILS[currentTier].name}</strong> to <strong>{PLAN_DETAILS[pendingPlan].name}</strong>.
              </Text>
              <Banner tone="warning" title="Feature Loss Warning">
                <BlockStack gap="200">
                  <Text as="p">By downgrading, you may lose access to:</Text>
                  <ul style={{ margin: "0", paddingLeft: "20px" }}>
                    {PLAN_DETAILS[currentTier].features
                      .filter((feature) => !PLAN_DETAILS[pendingPlan].features.includes(feature))
                      .map((feature) => (
                        <li key={feature}>
                          <Text as="span" variant="bodySm">
                            {feature}
                          </Text>
                        </li>
                      ))}
                  </ul>
                </BlockStack>
              </Banner>
              <Text as="p" tone="subdued">
                Your new rate of ${PLAN_DETAILS[pendingPlan].price}/mo will take effect on your next billing cycle.
              </Text>
            </>
          ) : pendingPlan ? (
            <>
              <Text as="p">
                Excellent choice! You are upgrading to the <strong>{PLAN_DETAILS[pendingPlan].name}</strong> plan (${PLAN_DETAILS[pendingPlan].price}/mo).
              </Text>
              <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                <BlockStack gap="200">
                  <Text as="p" fontWeight="bold">
                    New features you'll unlock:
                  </Text>
                  <ul style={{ margin: "0", paddingLeft: "20px" }}>
                    {PLAN_DETAILS[pendingPlan].features
                      .filter((feature) => !PLAN_DETAILS[currentTier].features.includes(feature))
                      .map((feature) => (
                        <li key={feature}>
                          <Text as="span" variant="bodySm">
                            {feature}
                          </Text>
                        </li>
                      ))}
                  </ul>
                </BlockStack>
              </Box>
              <Text as="p" tone="subdued">
                Upgrading results in an immediate pro-rated charge. New features are activated instantly.
              </Text>
            </>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

type ProviderModalProps = {
  loading: boolean;
  open: boolean;
  pendingProviderTier: TierKey | null;
  promoCode: string;
  rememberProvider: boolean;
  selectedProvider: Provider;
  showPromoField: boolean;
  lemonSqueezyLogo: string;
  stripeLogo: string;
  onClose: () => void;
  onContinue: () => void | Promise<void>;
  setPromoCode: (value: string) => void;
  setRememberProvider: (value: boolean) => void;
  setSelectedProvider: (value: Provider) => void;
  setShowPromoField: React.Dispatch<React.SetStateAction<boolean>>;
};

export function ProviderModal({
  loading,
  open,
  pendingProviderTier,
  promoCode,
  rememberProvider,
  selectedProvider,
  showPromoField,
  lemonSqueezyLogo,
  stripeLogo,
  onClose,
  onContinue,
  setPromoCode,
  setRememberProvider,
  setSelectedProvider,
  setShowPromoField,
}: ProviderModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Choose your payment method">
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" variant="bodySm" tone="subdued">
            Select how you'd like to pay. This determines your billing provider and currency.
          </Text>
          <InlineStack gap="400" blockAlign="stretch">
            <div
              onClick={() => setSelectedProvider("stripe")}
              className="theme-provider-card theme-provider-card-stripe"
              data-selected={selectedProvider === "stripe"}
            >
              {selectedProvider === "stripe" && (
                <div className="theme-provider-check">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="var(--reach-provider-stripe)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              <BlockStack gap="300">
                <img src={stripeLogo} alt="Stripe" style={{ height: 20, width: "auto", filter: "brightness(0) invert(1)" }} />
                <BlockStack gap="100">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    <span style={{ color: "white" }}>Canadian credit or debit card</span>
                  </Text>
                  <Text as="p" variant="bodySm">
                    <span style={{ color: "rgba(255,255,255,0.75)" }}>Best for Canadian merchants. Billed in CAD.</span>
                  </Text>
                </BlockStack>
                <span className="theme-provider-label">CAD</span>
              </BlockStack>
            </div>

            <div
              onClick={() => setSelectedProvider("lemonsqueezy")}
              className="theme-provider-card theme-provider-card-ls"
              data-selected={selectedProvider === "lemonsqueezy"}
            >
              {selectedProvider === "lemonsqueezy" && (
                <div className="theme-provider-check">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="var(--reach-provider-ls)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                  <img src={lemonSqueezyLogo} alt="" aria-hidden="true" style={{ height: 22, width: "auto" }} />
                  <span style={{ color: "white", fontWeight: 600, fontSize: 14 }}>Lemon Squeezy</span>
                </InlineStack>
                <BlockStack gap="100">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    <span style={{ color: "white" }}>International credit or debit card</span>
                  </Text>
                  <Text as="p" variant="bodySm">
                    <span style={{ color: "rgba(255,255,255,0.75)" }}>For merchants outside Canada. Tax handled automatically. Billed in USD.</span>
                  </Text>
                </BlockStack>
                <span className="theme-provider-label">USD</span>
              </BlockStack>
            </div>
          </InlineStack>

          <BlockStack gap="200">
            <button onClick={() => setShowPromoField((value) => !value)} className="theme-plain-btn" style={{ textAlign: "left" }}>
              <Text as="span" variant="bodyXs" tone="subdued">
                {showPromoField ? "▾" : "▸"} Have a promo code?
              </Text>
            </button>
            {showPromoField && (
              <TextField
                label=""
                labelHidden
                placeholder="Enter promo code"
                value={promoCode}
                onChange={setPromoCode}
                autoComplete="off"
              />
            )}
          </BlockStack>

          <Checkbox
            label="Remember my choice for future payments"
            checked={rememberProvider}
            onChange={setRememberProvider}
          />
        </BlockStack>
      </Modal.Section>
      <Modal.Section>
        <InlineStack align="end" gap="300">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!selectedProvider || !pendingProviderTier} loading={loading} onClick={onContinue}>
            Continue to Checkout
          </Button>
        </InlineStack>
      </Modal.Section>
    </Modal>
  );
}

type WipeBrainLibraryModalProps = {
  confirmText: string;
  loading: boolean;
  open: boolean;
  shopDomain: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  onConfirmTextChange: (value: string) => void;
  onExportCSV: () => void;
  onExportJSON: () => void;
};

export function WipeBrainLibraryModal({
  confirmText,
  loading,
  open,
  shopDomain,
  onClose,
  onConfirm,
  onConfirmTextChange,
  onExportCSV,
  onExportJSON,
}: WipeBrainLibraryModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Wipe Brain Library?"
      primaryAction={{
        content: "Yes, Wipe Everything",
        onAction: onConfirm,
        loading,
        destructive: true,
        disabled: confirmText !== shopDomain,
      }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Banner tone="warning" title="Back up your library first">
            <p>We recommend exporting your trained topics before wiping. You can re-import them later if needed.</p>
            <div style={{ marginTop: "8px" }}>
              <InlineStack gap="100">
                <Button size="slim" onClick={onExportJSON}>
                  Export JSON
                </Button>
                <Button size="slim" onClick={onExportCSV}>
                  Export CSV
                </Button>
              </InlineStack>
            </div>
          </Banner>
          <Text as="p">
            This will permanently delete <strong>all</strong> trained topics, scanned content, and AI vectors. This action cannot be undone.
          </Text>
          <Banner tone="critical" title="Danger Zone">
            <p>Your Chatbot will lose all custom knowledge immediately and revert to default behavior.</p>
            <div style={{ marginTop: "12px" }}>
              <Text as="p" variant="bodyMd" tone="base">
                Type your store domain to confirm deletion.
              </Text>
            </div>
            <div style={{ marginTop: "8px" }}>
              <div style={{ color: "#111827" }}>
                <TextField
                  label="Store domain"
                  value={confirmText}
                  onChange={onConfirmTextChange}
                  placeholder={shopDomain}
                  autoComplete="off"
                />
              </div>
            </div>
          </Banner>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

type WipeAnalyticsModalProps = {
  confirmText: string;
  loading: boolean;
  open: boolean;
  shopDomain: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  onConfirmTextChange: (value: string) => void;
};

export function WipeAnalyticsModal({
  confirmText,
  loading,
  open,
  shopDomain,
  onClose,
  onConfirm,
  onConfirmTextChange,
}: WipeAnalyticsModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirm Permanent Deletion"
      primaryAction={{
        content: "Delete All Data",
        onAction: onConfirm,
        loading,
        destructive: true,
        disabled: confirmText !== shopDomain,
      }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p">
            This will permanently delete <strong>all</strong> analytics data for this store, including visitor counts, channel clicks, queries, and engagement history. This action cannot be undone.
          </Text>
          <Banner tone="critical" title="Danger Zone">
            <p>Your analytics history will be permanently removed and cannot be recovered.</p>
            <div style={{ marginTop: "12px" }}>
              <Text as="p" variant="bodyMd" tone="base">
                Type your store domain to confirm deletion.
              </Text>
            </div>
            <div style={{ marginTop: "8px" }}>
              <div style={{ color: "#111827" }}>
                <TextField
                  label="Store domain"
                  value={confirmText}
                  onChange={onConfirmTextChange}
                  placeholder={shopDomain}
                  autoComplete="off"
                />
              </div>
            </div>
          </Banner>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

type ImportResultsModalProps = {
  open: boolean;
  total: number;
  inserted: string[];
  updated: string[];
  skipped: number;
  onClose: () => void;
};

export function ImportResultsModal({
  open,
  total,
  inserted,
  updated,
  skipped,
  onClose,
}: ImportResultsModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Complete"
      primaryAction={{ content: "Done", onAction: onClose }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p">
            Processed <strong>{total}</strong> topic{total === 1 ? "" : "s"}.
          </Text>

          {inserted.length > 0 ? (
            <Box background="bg-surface-secondary" padding="300" borderRadius="200">
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Added ({inserted.length})</Text>
                <div style={{ maxHeight: "140px", overflowY: "auto" }}>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {inserted.join(", ")}
                  </Text>
                </div>
              </BlockStack>
            </Box>
          ) : null}

          {updated.length > 0 ? (
            <Box background="bg-surface-secondary" padding="300" borderRadius="200">
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Updated ({updated.length})</Text>
                <div style={{ maxHeight: "140px", overflowY: "auto" }}>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {updated.join(", ")}
                  </Text>
                </div>
              </BlockStack>
            </Box>
          ) : null}

          {skipped > 0 ? (
            <Banner tone="info" title={`${skipped} topic${skipped === 1 ? "" : "s"} unchanged`}>
              <Text as="p" variant="bodySm">
                These matched existing titles, but nothing had changed, so they were skipped.
              </Text>
            </Banner>
          ) : null}

          {inserted.length === 0 && updated.length === 0 && skipped > 0 ? (
            <Text as="p" tone="subdued">
              Everything in this file already matched your current Brain Library.
            </Text>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

type DeleteTopicModalProps = {
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export function DeleteTopicModal({ loading, open, onClose, onConfirm }: DeleteTopicModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete Topic?"
      primaryAction={{ content: "Delete", destructive: true, loading, onAction: onConfirm }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <Text as="p">Are you sure you want to delete this trained topic? This cannot be undone.</Text>
      </Modal.Section>
    </Modal>
  );
}

type HolidayModalProps = {
  editingHolidayId: string | null;
  hName: string;
  localHolidays: Array<string | HolidayEntry>;
  month: number;
  open: boolean;
  selectedDates: { start: Date; end: Date };
  year: number;
  onAddOrUpdate: () => void;
  onChangeEditingHolidayId: (value: string | null) => void;
  onChangeHName: (value: string) => void;
  onChangeLocalHolidays: React.Dispatch<React.SetStateAction<Array<string | HolidayEntry>>>;
  onChangeMonth: (month: number, year: number) => void;
  onChangeSelectedDates: (value: { start: Date; end: Date }) => void;
  onClose: () => void;
  onDeleteStringHoliday: (index: number) => void;
  onEditHoliday: (holiday: HolidayEntry) => void;
  onSave: () => void;
};

export function HolidayModal({
  editingHolidayId,
  hName,
  localHolidays,
  month,
  open,
  selectedDates,
  year,
  onAddOrUpdate,
  onChangeEditingHolidayId,
  onChangeHName,
  onChangeLocalHolidays,
  onChangeMonth,
  onChangeSelectedDates,
  onClose,
  onDeleteStringHoliday,
  onEditHoliday,
  onSave,
}: HolidayModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Holiday Calendar"
      size="large"
      primaryAction={{ content: "Save Holidays", onAction: onSave }}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <Grid>
          <Grid.Cell columnSpan={{ xs: 6, md: 4 }}>
            <BlockStack gap="400">
              <Box padding="400" background="bg-surface-secondary" borderRadius="300">
                <BlockStack gap="400">
                  <Text as="h3" variant="headingSm">
                    Add New Holiday
                  </Text>
                  <TextField
                    label="Holiday Name"
                    value={hName}
                    onChange={onChangeHName}
                    placeholder="e.g. Summer Break"
                    autoComplete="off"
                  />
                  <Box background="bg-surface" padding="200" borderRadius="200" borderWidth="025" borderColor="border">
                    <DatePicker
                      month={month}
                      year={year}
                      onChange={onChangeSelectedDates}
                      onMonthChange={onChangeMonth}
                      selected={selectedDates}
                      allowRange
                    />
                  </Box>
                  <Button fullWidth variant="primary" disabled={!hName} onClick={onAddOrUpdate}>
                    {editingHolidayId ? "Update Holiday" : "Add to List"}
                  </Button>
                  {editingHolidayId && (
                    <Button
                      fullWidth
                      variant="tertiary"
                      onClick={() => {
                        onChangeEditingHolidayId(null);
                        onChangeHName("");
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </BlockStack>
              </Box>
            </BlockStack>
          </Grid.Cell>
          <Grid.Cell columnSpan={{ xs: 6, md: 2 }}>
            <BlockStack gap="400">
              <Text as="h3" variant="headingSm">
                Current Entries
              </Text>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {localHolidays.length === 0 ? (
                  <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                    <div style={{ textAlign: "center" }}>
                      <Text as="p" tone="subdued">
                        No holidays set yet.
                      </Text>
                    </div>
                  </Box>
                ) : (
                  <BlockStack gap="200">
                    {localHolidays.map((holiday, index) => {
                      if (typeof holiday === "string") {
                        return (
                          <Box key={index} background="bg-surface-secondary" padding="200" borderRadius="200">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="span" variant="bodySm">
                                {holiday}
                              </Text>
                              <Button icon={DeleteIcon} size="micro" variant="tertiary" tone="critical" onClick={() => onDeleteStringHoliday(index)} />
                            </InlineStack>
                          </Box>
                        );
                      }
                      return (
                        <Box key={holiday.id} background="bg-surface-secondary" padding="200" borderRadius="200">
                          <BlockStack gap="100">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="span" variant="bodySm" fontWeight="bold">
                                {holiday.name}
                              </Text>
                              <InlineStack gap="100">
                                <Button icon={EditIcon} size="micro" variant="tertiary" onClick={() => onEditHoliday(holiday)} />
                                <Button
                                  icon={DeleteIcon}
                                  size="micro"
                                  variant="tertiary"
                                  tone="critical"
                                  onClick={() => onChangeLocalHolidays((prev) => prev.filter((item) => typeof item === "string" || item.id !== holiday.id))}
                                />
                              </InlineStack>
                            </InlineStack>
                            <Text as="p" variant="bodyXs" tone="subdued">
                              {holiday.startDate === holiday.endDate ? holiday.startDate : `${holiday.startDate} to ${holiday.endDate}`}
                            </Text>
                          </BlockStack>
                        </Box>
                      );
                    })}
                  </BlockStack>
                )}
              </div>
            </BlockStack>
          </Grid.Cell>
        </Grid>
        <Box paddingBlockStart="400">
          <Banner tone="info" title="Quick Tips">
            <List>
              <List.Item>Click a date once to select a single day.</List.Item>
              <List.Item>Click a start date and then an end date to select a range.</List.Item>
              <List.Item>Holidays override your normal business hours and treat the store as closed.</List.Item>
            </List>
          </Banner>
        </Box>
      </Modal.Section>
    </Modal>
  );
}

type TrainingModalProps = {
  open: boolean;
  qContent: string;
  qCustomReply: string;
  duplicateTopic: { id: string; title?: string; question?: string } | null;
  qKeywords: string;
  qQuestion: string;
  qSuggestions: string;
  qTargetUrl: string;
  isReadOnly: boolean;
  selectedKbId: string | null;
  showPhase2Tiers: boolean;
  onClose: () => void;
  onEditDuplicateTopic: () => void;
  onEnableEdit: () => void;
  onSave: () => void | Promise<void>;
  setQContent: (value: string) => void;
  setQCustomReply: (value: string) => void;
  setQKeywords: (value: string) => void;
  setQQuestion: (value: string) => void;
  setQSuggestions: (value: string) => void;
  setQTargetUrl: (value: string) => void;
};

export function TrainingModal({
  open,
  qContent,
  qCustomReply,
  duplicateTopic,
  qKeywords,
  qQuestion,
  qSuggestions,
  qTargetUrl,
  isReadOnly,
  selectedKbId,
  showPhase2Tiers,
  onClose,
  onEditDuplicateTopic,
  onEnableEdit,
  onSave,
  setQContent,
  setQCustomReply,
  setQKeywords,
  setQQuestion,
  setQSuggestions,
  setQTargetUrl,
}: TrainingModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={selectedKbId ? (isReadOnly ? "View Topic" : "Update Topic") : "Train Chatbot"}
      primaryAction={isReadOnly ? { content: "Edit Topic", onAction: onEnableEdit } : { content: selectedKbId ? "Update Topic" : "Train Chatbot", onAction: onSave }}
      secondaryActions={[{ content: isReadOnly ? "Close" : "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {duplicateTopic ? (
            <Banner
              tone="warning"
              title={`This topic already exists as "${duplicateTopic.title || duplicateTopic.question || "Untitled topic"}"`}
              action={{ content: "View Existing Topic", onAction: onEditDuplicateTopic }}
            >
              <Text as="p" variant="bodySm">
                Reach keeps one topic per title. Open the existing topic to review it first, then use the in-modal edit action if you want to make changes.
              </Text>
            </Banner>
          ) : null}
          <TextField label="Question / Title" value={qQuestion} onChange={setQQuestion} placeholder="e.g. What is your return policy?" autoComplete="off" helpText="The main topic title used for matching." readOnly={isReadOnly} />
          <TextField
            label="Keywords"
            value={qKeywords}
            onChange={setQKeywords}
            placeholder="e.g. refund, return, money back, exchange"
            helpText="Comma-separated synonyms and alternate phrasings that also trigger this topic."
            autoComplete="off"
            readOnly={isReadOnly}
          />
          <TextField label="Chatbot Auto Reply" value={qCustomReply} onChange={setQCustomReply} multiline={4} placeholder="e.g. We offer a 30-day money-back guarantee..." autoComplete="off" readOnly={isReadOnly} />
          <TextField
            label="Follow-up Suggestions"
            value={qSuggestions}
            onChange={setQSuggestions}
            placeholder="e.g. Return Policy, Shipping Info"
            helpText="Comma-separated topic titles shown as quick-reply chips after this answer."
            autoComplete="off"
            readOnly={isReadOnly}
          />
          <TextField
            label="Target URL"
            value={qTargetUrl}
            onChange={setQTargetUrl}
            placeholder="e.g. https://yourstore.com/pages/shipping-policy"
            helpText="Optional page URL tied to this topic for navigation or reference."
            autoComplete="off"
            readOnly={isReadOnly}
          />

          <Divider />

          <BlockStack gap="200">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">
                {showPhase2Tiers ? "AI Sales Rep Enhancement" : "Scanned Data / Reference"}
              </Text>
              <Badge tone={showPhase2Tiers ? "magic" : undefined}>{showPhase2Tiers ? "Advanced" : "Reference"}</Badge>
            </InlineStack>
            <TextField
              label={showPhase2Tiers ? "Reference Context (for AI RAG)" : "Reference Text"}
              value={qContent}
              onChange={setQContent}
              multiline={4}
              placeholder={showPhase2Tiers ? "e.g. Detailed documentation or raw text the AI should learn from." : "e.g. Detailed documentation or raw text for the bot to reference."}
              helpText={
                showPhase2Tiers
                  ? "If Auto Reply is empty, the AI Sales Rep will use this text to generate a contextual response."
                  : "If Auto Reply is empty, the system will use this text to generate a response."
              }
              autoComplete="off"
              readOnly={isReadOnly}
            />
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
